import React, { useState } from 'react';
import { connect } from 'react-redux';

import { Button, makeStyles, Stepper, Step, StepLabel, Typography } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

import SelectTarget from './SelectTarget';
import ConfigureMerge from './ConfigureMerge';
import Review from './Review';
import ConfigureMultipleMerge from './ConfigureMultipleMerge';
import ReviewMultipleMerge from './ReviewMultipleMerge';
import Dialog from 'components/RMSDialog';

import { handleError } from 'reducers/ErrorReducer';
import { notifyDataUpdate } from 'reducers/DataUpdateReducer';
import { personMerge } from 'reducers/helpers/recordHelper';

const useStyles = makeStyles((theme) => ({
  steps: {
    width: '100%',
    marginBottom: 16,
  },
  button: {
    marginRight: theme.spacing(1),
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  nav: {
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
  },
  dialogWrap: {
    width: 1200,
    minHeight: 'calc(100vh - 250px)',
  },
}));

function getSteps() {
  return ['Select Person to Replace', 'Configure merge', 'Confirmation'];
}

function getStepContent(stepIndex, multi = false) {
  switch (stepIndex) {
    case 0:
      return 'Please select the person(s) to replace. The "Keep" person selected earlier will be updated, and the "Replace" person(s) choosen here will be deleted.';
    case 1:
      if (multi)
        return 'Persons can be edited or removed from the multi-merge. The selected "Keep" person will remain final.';
      else return 'Select fields you want to use to in the Keep person';
    case 2:
      return 'Please confirm that all data is correct. Use the back button to return to merge options.';
    default:
      return 'Unknown stepIndex';
  }
}

function PersonMerge(props) {
  const classes = useStyles();
  const steps = getSteps();

  const { close, targetID, rows, columns, showTargetPerson = false } = props;

  const [activeStep, setActiveStep] = useState(0);
  const [sourceID, setSourceID] = useState([]);
  const [source, setSource] = useState([]);
  const [target, setTarget] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [personData, setPersonData] = useState(null);
  const [deleteSource, setDeleteSource] = useState(true);
  const [validationErrors, setValidationErrors] = useState(false);
  const [deleteSources, setDeleteSources] = useState({});
  const [allRows, setAllRows] = useState(rows);

  const isNextActive = () => {
    if (loading || (validationErrors && activeStep === 2)) return false;
    switch (activeStep) {
      case 0:
        return sourceID !== null && sourceID.length > 0;
      case 1:
      case 2:
        return source.length > 0;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (activeStep === 2 && !window.confirm('Are you sure you want to merge?')) return;
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    if (source.length >= 1) {
      if (activeStep === 2 && source.length === 1) merge();
      if (activeStep === 2 && source.length > 1) multiMerge();
    }
  };

  const handleBack = () => {
    if (activeStep === 1) {
      setSourceID([]);
      setSource([]);
    }
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderActions = () => {
    return (
      <>
        <Button color="primary" onClick={close}>
          <CloseIcon /> Close
        </Button>
      </>
    );
  };

  const renderSteps = () => {
    return (
      <div className={classes.steps}>
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => {
            const stepProps = {};
            const labelProps = {};
            return (
              <Step key={label} {...stepProps}>
                <StepLabel {...labelProps}>{label}</StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <div>
          {activeStep === steps.length ? (
            <div>
              <Typography className={classes.instructions}>
                All steps completed - person merged
              </Typography>
            </div>
          ) : (
            <div className={classes.nav}>
              <Typography className={classes.instructions}>
                {getStepContent(activeStep, sourceID.length > 1)}
              </Typography>
              <div>
                <Button disabled={activeStep === 0} onClick={handleBack} className={classes.button}>
                  Back
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  className={classes.button}
                  disabled={!isNextActive()}>
                  {activeStep === steps.length - 1 ? 'Merge Persons' : 'Next'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const merge = async () => {
    const sourcePtsPersonID = source[0].ptsPersonID;

    const data = {
      sourcePtsPersonID,
      deleteSource,
      person: personData,
    };
    try {
      await personMerge(data);
      props.notifyDataUpdate({ type: 'Person' });
    } catch (err) {
      props.handleError(err);
    }
  };

  const multiMerge = async () => {
    const sourcePtsPersonIDs = source.map((s) => s.ptsPersonID);
    const data = { ...personData, sourcePtsPersonIDs, deleteSources, multiMerge: true };
    try {
      await personMerge(data);
      props.notifyDataUpdate({ type: 'Person' });
    } catch (err) {
      props.handleError(err);
      handleBack();
    }
  };

  return (
    <Dialog toolbar onClose={close} title="Person Merge" actions={renderActions()} maxWidth="lg">
      <div className={classes.dialogWrap}>
        {renderSteps()}
        {activeStep === 0 && (
          <SelectTarget
            showTargetPerson={showTargetPerson}
            rows={allRows}
            setRows={setAllRows}
            targetID={targetID}
            columns={columns}
            setSourceID={setSourceID}
            sourceID={sourceID}
          />
        )}
        {activeStep === 1 && sourceID.length === 1 && (
          <ConfigureMerge
            rows={allRows}
            targetID={targetID}
            sourceID={sourceID[0]}
            source={source}
            setSource={setSource}
            target={target}
            setTarget={setTarget}
            data={data}
            setData={setData}
            loading={loading}
            setLoading={setLoading}
          />
        )}
        {activeStep === 1 && sourceID.length > 1 && (
          <ConfigureMultipleMerge
            rows={allRows}
            sourceID={sourceID}
            targetID={targetID}
            source={source}
            setSource={setSource}
            target={target}
            setTarget={setTarget}
            data={data}
            setData={setData}
            loading={loading}
            setLoading={setLoading}
          />
        )}
        {activeStep === 2 && source.length === 1 && (
          <Review
            source={source[0]}
            target={target}
            data={data}
            loading={loading}
            setLoading={setLoading}
            personData={personData}
            setPersonData={setPersonData}
            deleteSource={deleteSource}
            setDeleteSource={setDeleteSource}
            setValidationErrors={setValidationErrors}
          />
        )}
        {activeStep === 2 && source.length > 1 && (
          <ReviewMultipleMerge
            source={source}
            target={target}
            data={data}
            loading={loading}
            setLoading={setLoading}
            personData={personData}
            setPersonData={setPersonData}
            deleteSources={deleteSources}
            setDeleteSources={setDeleteSources}
          />
        )}
      </div>
    </Dialog>
  );
}

export default connect(null, {
  handleError,
  notifyDataUpdate,
})(PersonMerge);
